import React, { useEffect, useId } from 'react';
import { X } from 'lucide-react';
import { Button } from './button';
import { ModalProps } from '@/types/component-props';

export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  className = "",
  showCloseButton = true,
  closeDisabled = false,
  backdropClassName = 'bg-gray-500 bg-opacity-75',
}: ModalProps) {
  const titleId = useId();

  // Escape closes the dialog, matching the backdrop click that was already
  // here — a keyboard route to an exit mouse users always had, rather than a
  // new way to lose work. Respects closeDisabled, so a dialog mid-write still
  // cannot be dismissed.
  useEffect(() => {
    if (!isOpen || closeDisabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeDisabled, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeDisabled) return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center p-4 z-50 overflow-y-auto ${backdropClassName}`}
      onClick={handleBackdropClick}
    >
      {/*
        role / aria-modal / aria-labelledby give assistive tech a dialog with a
        name; without them this announced as an unlabelled group. A focus trap
        is deliberately NOT added here — doing that properly is an app-wide
        focus-management change touching every dialog, and belongs in its own
        commit rather than riding along with a refund feature.
      */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={`bg-white rounded-lg shadow-lg w-full max-h-[90vh] overflow-y-auto ${className || 'max-w-md'}`}
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 id={titleId} className="text-xl font-semibold text-gray-900">{title}</h2>
              {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
            </div>
            {showCloseButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                disabled={closeDisabled}
                aria-label="Close dialog"
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
